import { uploadAdminFile } from '../../../services/viewerProjectService';
import { ASSET_UPLOAD_COPY } from '../components/sections/assetUploadCopy';
import type {
  DashboardAsset,
  DashboardAssetReadinessStatus,
  DashboardAssetType,
  DashboardRole,
} from '../types';

type DashboardAssetUploadEntityType = 'project' | 'tool' | 'part';
type DashboardAssetUploadScope = 'workspace' | 'project';

interface DashboardAssetUploadProjectContext {
  companyId: string;
  productId: string;
  entityId: string;
  entityType: DashboardAssetUploadEntityType;
}

interface DashboardAssetRuntimeRecord {
  assetId: string;
  companyId: string | null;
  productId: string | null;
  entityId: string | null;
  entityType: DashboardAssetUploadEntityType;
  downloadUrl: string;
  previewUrl: string | null;
  previewKind: DashboardAssetType | null;
  statusNotice: string;
  storageMode: 'backend' | 'local';
  updateNotice: string;
}

const dashboardAssetRuntimeRegistry = new Map<string, DashboardAssetRuntimeRecord>();

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  const formatted = value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${formatted} ${units[exponent]}`;
}

function fileHasExtension(fileName: string, extensions: string[]): boolean {
  const lowerName = fileName.trim().toLowerCase();
  return extensions.some((extension) => lowerName.endsWith(extension));
}

function assertUploadIsSupported(assetType: DashboardAssetType, file: File) {
  const acceptExtensions = ASSET_UPLOAD_COPY[assetType].accept
    .split(',')
    .map((extension) => extension.trim())
    .filter((extension) => extension.startsWith('.'));

  if (assetType === 'model' && !fileHasExtension(file.name, acceptExtensions)) {
    throw new Error('3D uploads currently support .glb files so the existing viewer stack can render them safely.');
  }

  if (assetType === 'image' && !fileHasExtension(file.name, acceptExtensions)) {
    throw new Error('Image uploads currently support PNG, JPG, JPEG, and WEBP files.');
  }

  if (assetType === 'pdf' && !fileHasExtension(file.name, acceptExtensions)) {
    throw new Error('Document uploads currently support PDF files.');
  }

  if (assetType === 'video' && !fileHasExtension(file.name, acceptExtensions)) {
    throw new Error('Video uploads currently support MP4, WEBM, and MOV files.');
  }

  if (assetType === 'cad' && !fileHasExtension(file.name, acceptExtensions)) {
    throw new Error('CAD uploads currently support DWG, DXF, STEP, and STP files.');
  }
}

function normalizeScopeValue(value: string): string {
  return value.trim();
}

function getSyncLabel(storageMode: DashboardAssetRuntimeRecord['storageMode']): string {
  return storageMode === 'backend' ? 'Backend synced' : 'Local preview only';
}

function buildStatusNotice(record: DashboardAssetRuntimeRecord): string {
  if (record.storageMode === 'backend') {
    if (record.entityId && record.entityType === 'project') {
      return `Synced to the persisted workspace file store and linked to project ${record.entityId}. Download is live from the backend content route.`;
    }

    return 'Synced to the persisted workspace file store. Download is live from the backend content route.';
  }

  return `Locally staged workspace-scoped asset preview. ${record.previewKind === 'model' ? 'Interactive GLB preview is active.' : 'Download works from the current browser session.'} Persisted backend sync is unavailable for this upload context.`;
}

function buildUpdateNotice(record: DashboardAssetRuntimeRecord): string {
  if (record.storageMode === 'backend') {
    return 'Update File stays deferred until the dashboard wires workspace-owned replace/version flows into this modal.';
  }

  return 'Update File stays deferred until this staged asset is connected to a persisted backend record.';
}

function registerRuntimeRecord(record: Omit<DashboardAssetRuntimeRecord, 'statusNotice' | 'updateNotice'>): DashboardAssetRuntimeRecord {
  const nextRecord: DashboardAssetRuntimeRecord = {
    ...record,
    statusNotice: '',
    updateNotice: '',
  };

  nextRecord.statusNotice = buildStatusNotice(nextRecord);
  nextRecord.updateNotice = buildUpdateNotice(nextRecord);
  dashboardAssetRuntimeRegistry.set(nextRecord.assetId, nextRecord);
  return nextRecord;
}

function canRevokeObjectUrl(): boolean {
  return (
    typeof URL !== 'undefined' &&
    typeof URL.revokeObjectURL === 'function'
  );
}

function isBlobUrl(url: string): boolean {
  return url.startsWith('blob:');
}

function revokeRuntimeRecordObjectUrl(record: DashboardAssetRuntimeRecord) {
  if (record.storageMode !== 'local') {
    return;
  }

  if (!isBlobUrl(record.downloadUrl) || !canRevokeObjectUrl()) {
    return;
  }

  URL.revokeObjectURL(record.downloadUrl);
}

function deleteRuntimeRecord(assetId: string) {
  const record = dashboardAssetRuntimeRegistry.get(assetId);
  if (!record) {
    return;
  }

  revokeRuntimeRecordObjectUrl(record);
  dashboardAssetRuntimeRegistry.delete(assetId);
}

function buildUploadedAsset(params: {
  assetId: string;
  assetType: DashboardAssetType;
  scope: DashboardAssetUploadScope;
  companyId: string | null;
  productId: string | null;
  entityId: string | null;
  entityType: DashboardAssetUploadEntityType;
  file: File;
  readiness: DashboardAssetReadinessStatus;
  storageMode: DashboardAssetRuntimeRecord['storageMode'];
}): DashboardAsset {
  const normalizedCompanyId = params.companyId?.trim();
  const normalizedProductId = params.productId?.trim();
  const normalizedEntityId = params.entityId?.trim();
  const projectContextLabel =
    normalizedCompanyId && normalizedProductId && normalizedEntityId
      ? `${normalizedCompanyId}/${normalizedProductId}/${normalizedEntityId}`
      : null;
  const scopeLabel =
    params.scope === 'project' ? projectContextLabel ?? 'Project-scoped asset' : 'Workspace library (default)';
  const metadata = [
    { label: 'Scope', value: scopeLabel },
    { label: 'Sync', value: getSyncLabel(params.storageMode) },
    {
      label: 'Source',
      value: params.storageMode === 'backend' ? 'Classified file API' : 'Session staging',
    },
  ];
  if (params.scope === 'workspace' && projectContextLabel) {
    metadata.push({
      label: 'Linked project context',
      value: projectContextLabel,
    });
  }

  return {
    id: params.assetId,
    name: params.file.name,
    type: params.assetType,
    readiness: params.readiness,
    sizeLabel: formatBytes(params.file.size),
    updatedAtLabel: 'Just now',
    description:
      params.storageMode === 'backend'
        ? `Uploaded into the persisted workspace file store for ${scopeLabel}.`
        : `Uploaded into the current dashboard session as a workspace-scoped asset.`,
    metadata,
  };
}

export function resetDashboardAssetRuntimeStateForTests() {
  for (const assetId of dashboardAssetRuntimeRegistry.keys()) {
    deleteRuntimeRecord(assetId);
  }
  assetThumbnailRegistry.clear();
}

// ── Thumbnail Registry ────────────────────────────────────────────
// Separate from the runtime record so thumbnail generation doesn't
// overwrite the model preview URL needed by the interactive viewer.

const assetThumbnailRegistry = new Map<string, string>();

/** Store a generated thumbnail blob URL for an asset. */
export function setAssetThumbnailUrl(assetId: string, blobUrl: string): void {
  assetThumbnailRegistry.set(assetId, blobUrl);
}

/** Get the thumbnail blob URL for an asset (if generated). */
export function getAssetThumbnailUrl(assetId: string): string | null {
  return assetThumbnailRegistry.get(assetId) ?? null;
}

/**
 * Seeds a runtime record for fixture/demo assets so the 3D viewer
 * can render without a real upload. Used by the visual regression
 * harness and local development.
 */
export function seedDashboardAssetRuntimeRecord(params: {
  assetId: string;
  previewUrl: string;
  previewKind: DashboardAssetType;
  companyId?: string;
  productId?: string;
}): void {
  registerRuntimeRecord({
    assetId: params.assetId,
    companyId: params.companyId ?? null,
    productId: params.productId ?? null,
    entityId: null,
    entityType: 'project',
    downloadUrl: params.previewUrl,
    previewUrl: params.previewUrl,
    previewKind: params.previewKind,
    storageMode: 'local',
  });
}

export function releaseDashboardAssetRuntimeRecords(assetIds: ReadonlyArray<string>) {
  for (const assetId of assetIds) {
    deleteRuntimeRecord(assetId);
  }
}

/**
 * Hydrates the runtime registry for API-loaded assets so the thumbnail
 * generator and 3D viewer can resolve model URLs for previously-uploaded files.
 *
 * Called after loading assets from the API. Only hydrates assets that don't
 * already have a runtime record (preserves upload-session records).
 */
export function hydrateDashboardAssetRuntimeRecords(
  assets: ReadonlyArray<DashboardAsset>,
  previewUrls: Map<string, string>,
): void {
  for (const asset of assets) {
    // Skip if already has a runtime record (e.g., from current upload session)
    if (dashboardAssetRuntimeRegistry.has(asset.id)) continue;

    const previewUrl = previewUrls.get(asset.id);
    if (!previewUrl) continue;

    // Only hydrate model assets (thumbnail generator only works with GLB)
    if (asset.type !== 'model') continue;

    registerRuntimeRecord({
      assetId: asset.id,
      companyId: null,
      productId: null,
      entityId: null,
      entityType: 'project',
      downloadUrl: previewUrl,
      previewUrl: previewUrl,
      previewKind: 'model',
      storageMode: 'backend',
    });
  }
}

export function getDashboardAssetRuntimeRecord(assetId: string): DashboardAssetRuntimeRecord | null {
  return dashboardAssetRuntimeRegistry.get(assetId) ?? null;
}

export function getDashboardAssetStatusNotice(asset: DashboardAsset): string {
  return (
    dashboardAssetRuntimeRegistry.get(asset.id)?.statusNotice ??
    'Seeded inventory asset. This dashboard record has no workspace-owned download URL yet, so download stays disabled until backend asset data is wired in.'
  );
}

export function getDashboardAssetPrimaryActionState(asset: DashboardAsset): { enabled: boolean; notice: string } {
  const runtimeRecord = dashboardAssetRuntimeRegistry.get(asset.id);
  if (!runtimeRecord?.downloadUrl) {
    return {
      enabled: false,
      notice: 'Seeded inventory asset. Download is not wired yet because this dashboard record has no live workspace asset URL.',
    };
  }

  return {
    enabled: true,
    notice:
      runtimeRecord.storageMode === 'backend'
        ? 'Downloads the backend-synced workspace file.'
        : 'Downloads the locally staged browser-session asset.',
  };
}

export function getDashboardAssetUpdateActionNotice(asset: DashboardAsset): string {
  return (
    dashboardAssetRuntimeRegistry.get(asset.id)?.updateNotice ??
    'Update File stays deferred until seeded assets are backed by editable project asset records in this dashboard shell.'
  );
}

export function downloadDashboardAsset(asset: DashboardAsset) {
  const runtimeRecord = dashboardAssetRuntimeRegistry.get(asset.id);
  if (!runtimeRecord?.downloadUrl) {
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = runtimeRecord.downloadUrl;
  anchor.download = asset.name;
  anchor.rel = 'noopener noreferrer';
  anchor.target = '_blank';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export function getDashboardAssetUploadAccessNotice(role: DashboardRole | null): string {
  if (role === 'client') {
    return 'Upload is available for clients in MVP. Assets uploaded from this page default to workspace scope.';
  }

  if (role === 'admin' || role === 'superadmin') {
    return 'Upload is available. Assets uploaded from this page default to workspace scope.';
  }

  return 'Upload requires authenticated admin or project-editor context.';
}

export function getDashboardAssetUploadModeNotice(params: {
  assetType: DashboardAssetType;
  companyId: string;
  productId: string;
  entityId: string;
}): string {
  void params.assetType;
  const companyId = normalizeScopeValue(params.companyId);
  const productId = normalizeScopeValue(params.productId);
  const entityId = normalizeScopeValue(params.entityId);
  const hasProjectScope = companyId.length > 0 && productId.length > 0 && entityId.length > 0;

  if (hasProjectScope) {
    return 'This upload is workspace-scoped by default and will retain linked project context metadata.';
  }

  return 'This upload is workspace-scoped by default from the Assets page.';
}

export async function createDashboardUploadedAsset(params: {
  assetType: DashboardAssetType;
  scope?: DashboardAssetUploadScope;
  companyId: string;
  productId: string;
  entityType: DashboardAssetUploadEntityType;
  entityId: string;
  projectContext?: DashboardAssetUploadProjectContext;
  file: File;
}): Promise<DashboardAsset> {
  assertUploadIsSupported(params.assetType, params.file);

  const scope = params.scope ?? 'workspace';
  const projectContext = params.projectContext ?? {
    companyId: params.companyId,
    productId: params.productId,
    entityId: params.entityId,
    entityType: params.entityType,
  };
  const companyId = normalizeScopeValue(projectContext.companyId);
  const productId = normalizeScopeValue(projectContext.productId);
  const entityId = normalizeScopeValue(projectContext.entityId);
  const linkedProjectId =
    scope === 'project' && projectContext.entityType === 'project' && entityId.length > 0
      ? entityId
      : null;

  try {
    const uploadedFile = await uploadAdminFile({
      file: params.file,
      projectId: linkedProjectId,
    });
    const previewKind = params.assetType === 'cad' ? null : params.assetType;
    const assetId = uploadedFile.fileId;

    registerRuntimeRecord({
      assetId,
      companyId: companyId || null,
      productId: productId || null,
      entityId: linkedProjectId ?? (entityId || null),
      entityType: projectContext.entityType,
      downloadUrl: uploadedFile.contentUrl,
      previewUrl: previewKind ? uploadedFile.contentUrl : null,
      previewKind,
      storageMode: 'backend',
    });

    // Auto-assign model to viewer project when a 3D model is uploaded
    if (params.assetType === 'model' && companyId && productId) {
      try {
        const { assignModelToViewerProject } = await import('../../../services/viewerProjectService');
        const result = await assignModelToViewerProject(companyId, productId, assetId);
        console.info('[dashboard.assets] MODEL_AUTO_ASSIGNED', {
          assetId,
          companyId,
          productId,
          draftRevision: result.draftRevision,
        });
      } catch (assignError) {
        // Non-blocking: model assignment failure shouldn't prevent the upload from succeeding
        console.error('[dashboard.assets] MODEL_AUTO_ASSIGN_FAILED', {
          assetId,
          companyId,
          productId,
          message: assignError instanceof Error ? assignError.message : 'Model assignment failed.',
          error: assignError,
        });
      }
    }

    return buildUploadedAsset({
      assetId,
      assetType: params.assetType,
      scope,
      companyId: companyId || null,
      productId: productId || null,
      entityId: linkedProjectId ?? (entityId || null),
      entityType: projectContext.entityType,
      file: params.file,
      readiness: uploadedFile.readiness === 'ready' ? 'ready' : 'processing',
      storageMode: 'backend',
    });
  } catch (error) {
    console.error('[dashboard.assets] BACKEND_UPLOAD_FAILED', {
      assetType: params.assetType,
      scope,
      linkedProjectId,
      fileName: params.file.name,
      message: error instanceof Error ? error.message : 'Unable to upload asset right now.',
      error,
    });
    throw error;
  }
}
